// Bridge between `eventBusService` (raw SSE) and the rest of the app:
//
//   1. Mirrors the latest StreamEvent of each kind into the Jotai atoms
//      (`atomLastFollow` / `atomLastSub` / `atomLastDonation` / `atomLastRaid`).
//   2. Mirrors chat messages into `atomChatBuffer` (capped at 200).
//   3. Mirrors the SSE connection state into `atomEventBusActive`.
//   4. Persists each event to Dexie via `OverlayEventStatsRepository.record()`
//      so the `events.*` namespace (sessions/today/allTime counters) has data.
//   5. Forwards each event to the Studio's `TriggerEngine` so user-defined
//      triggers (`event_follow`, `event_sub`, ...) fire automatically when
//      the corresponding platform event arrives.
//
// `useEventBusBridge` is mounted ONCE at the app root (Phase 7 wires it).
// On unmount it tears down its handlers but keeps the underlying SSE
// service alive — re-mounting (e.g. on HMR or route change) just
// reattaches without dropping connections, and the buffer survives.
//
// The TriggerEngine forwarding uses `dispatchExternal` (added alongside
// this bridge) which is a thin public wrapper around the engine's
// internal `fireEvent` method. We chose `dispatchExternal` over making
// `fireEvent` public because (a) `fireEvent`'s payload signature is too
// narrow for `StreamEvent`/`ChatMessage` objects, and (b) keeping a
// separate entry point lets us isolate "external" event flow from
// internal rule-driven dispatches in logs/telemetry later.

import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";

import { atomAccountAddress } from "../../atoms/atomsAccount";
import {
  atomChatBuffer,
  atomEventBusActive,
  atomLastDonation,
  atomLastFollow,
  atomLastRaid,
  atomLastSub,
} from "../../atoms/atomsEventBus";
import { RadioInstance } from "../../entities/Radio";
import { overlayEventStatsRepository } from "../../infrastructure/Repositories/Overlay/OverlayEventStatsRepository";
import type { StreamEventKind } from "../../interfaces/OverlayStats";
import type { TriggerEvent } from "../../interfaces/Widgets";

import { eventBusService } from "./eventBusService";
import type { ChatMessage, StreamEvent } from "./types";

const CHAT_BUFFER_LIMIT = 200;

const triggerEventName = (kind: StreamEvent["kind"]): TriggerEvent => {
  switch (kind) {
    case "follow":
      return "event_follow";
    case "sub":
      return "event_sub";
    case "donation":
      return "event_donation";
    case "raid":
      return "event_raid";
  }
};

const persistEvent = (accountId: string, e: StreamEvent): void => {
  // Gift subs roll up into a separate `gift_sub` bucket so the
  // `events.giftSubCount` namespace can be queried independently of
  // regular subs.
  const kind: StreamEventKind =
    e.kind === "sub" && e.isGift ? "gift_sub" : (e.kind as StreamEventKind);
  const platform = (e.platform ?? "unknown").toLowerCase();
  overlayEventStatsRepository
    .record({
      accountId,
      kind,
      platform,
      amountUsd: e.amountUsd,
      amount: e.amount,
      username: e.user?.username,
      raidViewers: e.viewers,
      timestamp: e.timestamp,
    })
    .catch((error) => {
      console.error("[eventBusBridge] persist failed", error);
    });
  // Counts of `kind: "sub"` should include gift subs too, so when we
  // bucket a gift_sub also write the regular sub bucket. This keeps
  // `events.subCount` consistent with the "total subs delivered" intent.
  if (kind === "gift_sub") {
    overlayEventStatsRepository
      .record({
        accountId,
        kind: "sub",
        platform,
        amountUsd: e.amountUsd,
        amount: e.amount,
        username: e.user?.username,
        timestamp: e.timestamp,
      })
      .catch((error) => {
        console.error("[eventBusBridge] persist (sub mirror) failed", error);
      });
  }
};

const dispatchTrigger = (eventName: TriggerEvent, payload: unknown): void => {
  // RadioInstance is null until Radio.initialize() runs at app boot.
  // If we miss the first few events because the engine isn't up yet,
  // that's acceptable — atoms still update and Dexie still records.
  const engine = RadioInstance?.Triggers;
  if (!engine) return;
  try {
    engine.dispatchExternal(eventName, payload);
  } catch (err) {
    console.error("[eventBusBridge] dispatchExternal failed", err);
  }
};

/**
 * Mount once at the app root. Starts the SSE service and wires its
 * events to atoms, Dexie and the TriggerEngine for as long as the
 * component is mounted. The SSE handles outlive any single mount cycle.
 */
export const useEventBusBridge = (): void => {
  const accountId = useAtomValue(atomAccountAddress);
  const setLastFollow = useSetAtom(atomLastFollow);
  const setLastSub = useSetAtom(atomLastSub);
  const setLastDonation = useSetAtom(atomLastDonation);
  const setLastRaid = useSetAtom(atomLastRaid);
  const setChatBuffer = useSetAtom(atomChatBuffer);
  const setActive = useSetAtom(atomEventBusActive);

  useEffect(() => {
    eventBusService.start();

    const offEvent = eventBusService.onEvent((e: StreamEvent) => {
      switch (e.kind) {
        case "follow":
          setLastFollow(e);
          break;
        case "sub":
          setLastSub(e);
          break;
        case "donation":
          setLastDonation(e);
          break;
        case "raid":
          setLastRaid(e);
          break;
      }
      if (accountId) persistEvent(accountId, e);
      dispatchTrigger(triggerEventName(e.kind), e);
    });

    const offChat = eventBusService.onChat((c: ChatMessage) => {
      setChatBuffer((prev) => {
        const next =
          prev.length >= CHAT_BUFFER_LIMIT ? prev.slice(1) : [...prev];
        next.push(c);
        return next;
      });
      dispatchTrigger("event_chat", c);
    });

    const offConn = eventBusService.onConnection((v: boolean) => {
      setActive(v);
    });

    return () => {
      offEvent();
      offChat();
      offConn();
      // Intentional: do NOT call eventBusService.stop() here.
      // The SSE handles are app-lifetime and unmount/remount of the
      // root (e.g. HMR) shouldn't tear them down.
    };
  }, [
    accountId,
    setLastFollow,
    setLastSub,
    setLastDonation,
    setLastRaid,
    setChatBuffer,
    setActive,
  ]);
};
